// Words for a price check — shared by the panel, the edit-page bar, the card
// chips and the Overview rows so the same verdict never reads two ways.
import dayjs from "dayjs";
import { formatMoneyMajor } from "../../core/currency";
import { getFileUrl } from "../../core/config/api";
import { ordinal } from "./priceCheck";

// The listing's own currency labels its prices (NULL = legacy GBP rows — the
// same rule the listing card applies).
export const listingCurrency = (listing) => listing?.currency || "gbp";

// The edit page opens on the matching sales-channel tab (RCListingForm reads ?channel=).
export const editPriceUrl = (listingId, mp) => `/product/${listingId}/edit/listings?channel=${mp}`;

// Brand / model / reference / photo — resolved the way RCListingCard does.
export const listingIdentity = (listing) => {
  const product = listing?.product;
  const pick = (v) => v?.name || (typeof v === "string" ? v : null);
  return {
    brand: pick(product?.brand),
    model: pick(product?.model),
    reference: product?.reference_number || listing?.reference_number || null,
    image: getFileUrl(listing?.image_id) || getFileUrl(product?.images?.[0]?.id) || null,
  };
};

export const formatPrice = (amount, currency) =>
  amount === null || amount === undefined || !Number.isFinite(Number(amount))
    ? "—"
    : formatMoneyMajor(Number(amount), { currency, decimals: 0 });

export const formatShortDate = (date) => (date ? dayjs(date).format("D MMM") : "—");
export const formatCheckedAt = (date) => (date ? dayjs(date).format("D MMM, HH:mm") : "—");

/** { title, body } for the verdict card / bar. */
export const verdictCopy = (check, currency) => {
  const fmt = (v) => formatPrice(v, currency);
  const { label } = check;
  switch (check.state) {
    case "above": {
      const nth = check.topCount === 5 ? "5th" : ordinal(check.topCount);
      return {
        title: `${fmt(check.gap)} above market`,
        body: `The ${nth}-cheapest on ${label} is ${fmt(check.fifth)}. Lower your price to be in the top ${check.topCount}.`,
      };
    }
    case "below":
      return {
        title: `${fmt(check.gap)} below market`,
        body: `Cheaper than every listing found on ${label} (the cheapest is ${fmt(check.lowest)}). Raising it to ${fmt(check.suggestedPrice)} keeps you the cheapest.`,
      };
    case "in_range":
      return {
        title: "In range",
        body: `You're the ${ordinal(check.rank)} cheapest of the ${check.topCount} lowest prices on ${label}.`,
      };
    case "reviewed":
      return {
        title: `Reviewed until ${formatShortDate(check.reviewedUntil)}`,
        body: `Hidden from the price review until then. The nightly check flags it again after that if it is still out of range.`,
      };
    case "no_data":
      // The search ran and found nothing / one priced listing: say so, with the date.
      if (check.count === 1 && check.checkedAt) {
        return {
          title: "Only 1 listing found",
          body: `Checked ${formatShortDate(check.checkedAt)}: the ${label} search found 1 priced listing — at least 2 are needed to compare, so nothing is flagged. Open the search to see it.`,
        };
      }
      if (check.count === 0 && check.checkedAt) {
        return {
          title: "No listings found",
          body: `Checked ${formatShortDate(check.checkedAt)}: the ${label} search found no priced listings, so there's nothing to compare. Open the search to see what it looked for.`,
        };
      }
      return {
        title: "No data yet",
        body: check.price === null
          ? `There's no ${label} price on this listing, so there's nothing to compare. Nothing is flagged without data.`
          : `Fewer than 2 comparable listings were found on ${label}, so there's nothing to compare against. Nothing is flagged without data.`,
      };
    default:
      return {
        title: "Not checked yet",
        body: `${label} hasn't been checked for this listing yet. The check runs nightly.`,
      };
  }
};

/** Short chip label, e.g. "Chrono24 · £1,250 above". */
export const chipLabel = (check, currency) => {
  const fmt = (v) => formatPrice(v, currency);
  switch (check.state) {
    case "above": return `${check.label} · ${fmt(check.gap)} above`;
    case "below": return `${check.label} · ${fmt(check.gap)} below`;
    case "in_range": return `${check.label} · in range`;
    case "reviewed": return `Reviewed · until ${formatShortDate(check.reviewedUntil)}`;
    case "no_data":
      if (check.count === 0 && check.checkedAt) return `${check.label} · no listings found · ${formatShortDate(check.checkedAt)}`;
      if (check.count === 1 && check.checkedAt) return `${check.label} · only 1 listing found · ${formatShortDate(check.checkedAt)}`;
      return `${check.label} · no data`;
    default: return `${check.label} · not checked`;
  }
};

/** The six figures — Lowest, 5th lowest, Median, Avg of 5 lowest, Listings found, Last checked. */
export const priceFigures = (check, currency) => {
  const fmt = (v) => formatPrice(v, currency);
  const nth = check.topCount === 5 || !check.topCount ? "5th" : ordinal(check.topCount);
  return [
    ["Lowest", fmt(check.lowest)],
    [`${nth} lowest`, fmt(check.fifth)],
    ["Median", fmt(check.median)],
    ["Avg of 5 lowest", fmt(check.avg5)],
    ["Listings found", check.count ?? "—"],
    ["Last checked", formatCheckedAt(check.checkedAt)],
  ];
};
